import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaStar,
  FaMapMarkerAlt,
  FaEye,
  FaFileAlt,
  FaPaperPlane,
  FaShieldAlt,
} from 'react-icons/fa';
import CommonService from '../../services/common.service';
import {
  LOGISTICS_COMPANY_TYPE_FILTER_OPTIONS,
  LOGISTICS_CORE_SPECIALIZATION,
  LOGISTICS_INDUSTRIES_SERVED,
  LOGISTICS_PREFERRED_SHIPMENT_TYPES,
  LOGISTICS_SPECIAL_CARGO,
  LOGISTICS_TRANSPORT_MODES,
} from '../../constants/logisticsProfileFormOptions';
import { API_URLS, resolvePublicFileUrl } from '../../constants/api-urls';
import StorageService from '../../services/StorageService';
import B2BHubHomeHeader from '../../components/B2BHubHomeHeader';
import HubHomeFooter from '../../components/hub/HubHomeFooter';
import LogisticsRfqTypeSelectionModal from '../../components/logistics/LogisticsRfqTypeSelectionModal';
import { setLogisticsRfqSubmittedSessionFlag } from './logisticsRfqBrowseFlags';
import { buildLogisticsViewRfqPath, buildLogisticsViewRfqPathFromIds } from './logisticsRfqSubmissionStorage';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/home.scss';
import '../../assets/styles/logistics_providers_browse.scss';
import '../../assets/styles/logistics_rfq_selection.scss';
import { readSearchQueryFromParams } from '../../config/globalHeaderSearch';

export default function LogisticsProvidersBrowsePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rfqSuccessToastShown = useRef(false);

  /** Provider company id when Send RFQ modal is open (null = closed). */
  const [rfqModalCompanyId, setRfqModalCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(() => readSearchQueryFromParams(searchParams));
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedIndustryFilters, setSelectedIndustryFilters] = useState([]);
  const [selectedStateFilter, setSelectedStateFilter] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [selectedCoreSpecFilters, setSelectedCoreSpecFilters] = useState([]);
  const [selectedSpecialCargoFilters, setSelectedSpecialCargoFilters] = useState([]);
  const [selectedShipmentTypeFilters, setSelectedShipmentTypeFilters] = useState([]);
  const [selectedModeFilters, setSelectedModeFilters] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  /** Logged-in buyer: provider company id → { id, rfqReferenceNumber } from API (persists across refresh). */
  const [mySubmissionsByProviderId, setMySubmissionsByProviderId] = useState({});
  /** Bumps after a successful RFQ submit redirect so we refetch `/my-submissions` for button state. */
  const [mySubmissionsVersion, setMySubmissionsVersion] = useState(0);

  const pageTitle = 'Logistics Providers';

  const doRetry = () => {
    setError(null);
    setRetryCount((c) => c + 1);
  };

  useEffect(() => {
    setSearch(readSearchQueryFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    const msg = location.state?.rfqSubmittedMessage;
    if (typeof msg !== 'string' || !msg.trim()) {
      rfqSuccessToastShown.current = false;
      return;
    }
    setLogisticsRfqSubmittedSessionFlag();
    if (rfqSuccessToastShown.current) return;
    rfqSuccessToastShown.current = true;
    toast.success(msg.trim());
    setMySubmissionsVersion((v) => v + 1);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    const token = StorageService.getToken();
    if (!token) {
      setMySubmissionsByProviderId({});
      return undefined;
    }
    (async () => {
      try {
        const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSIONS);
        const resBody = res?.data ?? res ?? {};
        const nested = resBody?.data ?? resBody;
        const raw = Array.isArray(nested?.items) ? nested.items : [];
        const items = raw;
        const map = {};
        for (const row of items) {
          const cid = row.logisticsProviderCompanyId ?? row.logistics_provider_company_id;
          const ref = row.rfqReferenceNumber ?? row.rfq_reference_number;
          if (cid != null && row.id != null && ref) {
            map[Number(cid)] = { id: row.id, rfqReferenceNumber: String(ref).trim() };
          }
        }
        if (!cancelled) setMySubmissionsByProviderId(map);
      } catch {
        if (!cancelled) setMySubmissionsByProviderId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retryCount, location.key, mySubmissionsVersion]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const fetchList = async () => {
      setLoading(true);
      try {
        const params = { page: 1, limit: 50 };
        const listRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.LIST, params);
        const data = listRes?.data ?? listRes;
        const items = Array.isArray(data?.items) ? data.items : [];
        const totalCount = data?.total ?? items.length;
        if (cancelled) return;
        setList(items);
        setTotal(totalCount);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setList([]);
          setTotal(0);
          const status = err?.response?.status;
          const msg = err?.response?.data?.message || err?.message;
          const statusHint = status ? ` (${status})` : '';
          setError(
            msg
              ? `${msg}${statusHint}`
              : `Unable to load list${statusHint}. Check that the backend is running and the API URL is correct.`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [retryCount]);

  const normalizeImageRef = (ref) => {
    if (ref == null || ref === '') return null;
    if (typeof ref === 'string') {
      const t = ref.trim();
      return t || null;
    }
    if (typeof ref === 'object') {
      const u =
        ref.url ||
        ref.accessPath ||
        ref.filePath ||
        ref.src ||
        ref.path ||
        ref.imageUrl ||
        ref.image_url;
      return u && String(u).trim() ? String(u).trim() : null;
    }
    return null;
  };

  const getCardImage = (item) => {
    const bi = item.basic_information || {};
    const logo = normalizeImageRef(bi.companyLogo || bi.company_logo);
    if (logo) return logo;
    return null;
  };

  const isFeaturedProvider = (item) => {
    const bi = item.basic_information || {};
    return Boolean(
      item.featured || bi.featured || bi.isFeatured || bi.is_featured || bi.listing_featured || bi.listingFeatured
    );
  };

  const getRatingDisplay = (item) => {
    const bi = item.basic_information || {};
    const raw = bi.averageRating ?? bi.average_rating ?? item.rating_score ?? item.rating;
    const n = raw != null && raw !== '' ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0) return null;
    return n.toFixed(1);
  };

  const getReviewCount = (item) => {
    const bi = item.basic_information || {};
    const n = bi.reviewsCount ?? bi.reviews_count ?? item.review_count;
    const num = n != null && n !== '' ? parseInt(String(n), 10) : NaN;
    if (!Number.isFinite(num) || num < 0) return null;
    return num;
  };

  const looksLikeLocationId = (v) => {
    if (v == null || v === '') return false;
    const s = String(v).trim();
    return /^\d+$/.test(s);
  };

  const getLocation = (item) => {
    const bi = item.basic_information || {};
    const hq = String(bi.headquartersLocation || bi.headquarters_location || '').trim();
    if (hq) return hq;

    const addr = item.address;
    if (typeof addr === 'string' && addr.trim()) return addr.trim();
    if (addr && typeof addr === 'object') {
      const ac = addr.city;
      const as = addr.state;
      const acountry = addr.country;
      if (!looksLikeLocationId(ac) && !looksLikeLocationId(as)) {
        const parts = [ac, as, acountry].filter(Boolean);
        if (parts.length) return parts.join(', ');
      }
      const partsNamed = [
        addr.city_name || addr.cityName,
        addr.state_name || addr.stateName,
        addr.country_name || addr.countryName,
      ].filter(Boolean);
      if (partsNamed.length) return partsNamed.join(', ');
    }
    return '';
  };

  const getItemStateForFilter = (item) => {
    const bi = item.basic_information || {};
    const hq = String(bi.headquartersLocation || bi.headquarters_location || '').trim();
    if (hq) {
      const parts = hq.split(',').map((s) => s.trim()).filter(Boolean);
      if (parts.length >= 2) return parts[parts.length - 1];
      return '';
    }
    const addr = item.address;
    if (addr && typeof addr === 'object') {
      const st = addr.state_name || addr.stateName || addr.state;
      if (st && !looksLikeLocationId(st)) return String(st).trim();
    }
    return '';
  };

  const normalizeProfileStringArray = (raw) => {
    if (!Array.isArray(raw)) return [];
    const out = [];
    raw.forEach((x) => {
      const v =
        typeof x === 'string'
          ? x.trim()
          : String(x?.value ?? x?.label ?? x?.name ?? '').trim();
      if (v) out.push(v);
    });
    return out;
  };

  const getIndustryTags = (item) => {
    const cap = item.capabilities || {};
    const raw = cap.industriesServed || cap.industries_served || [];
    return normalizeProfileStringArray(raw).slice(0, 3);
  };

  const getItemIndustryValues = (item) => {
    const cap = item.capabilities || {};
    const raw = cap.industriesServed || cap.industries_served || [];
    return normalizeProfileStringArray(raw);
  };

  const getItemCoreSpecializationValues = (item) => {
    const cap = item.capabilities || {};
    const raw = cap.coreSpecialization || cap.core_specialization || [];
    return normalizeProfileStringArray(raw);
  };

  const getItemSpecialCargoValues = (item) => {
    const cap = item.capabilities || {};
    const raw =
      cap.specialCargoCapabilities ||
      cap.special_cargo_capabilities ||
      [];
    return normalizeProfileStringArray(raw);
  };

  const getItemPreferredShipmentTypes = (item) => {
    const tm = item.transport_modes || {};
    const raw = tm.preferredShipmentTypes || tm.preferred_shipment_types || [];
    return normalizeProfileStringArray(raw);
  };

  const getTransportModes = (item) => {
    const tm = item.transport_modes || {};
    const modes = Array.isArray(tm.modes) ? tm.modes : [];
    return modes.map((m) => String(m).toLowerCase());
  };

  const formatMetric = (value) => (value == null || value === '' ? '—' : value);

  const getCompanyTypeDisplay = (item) => {
    const bi = item.basic_information || {};
    const t = String(bi.companyType ?? bi.company_type ?? '').trim();
    return t || null;
  };

  /** Years in business — profile Basic Information. */
  const getExperienceDisplay = (item) => {
    const bi = item.basic_information || {};
    const y = bi.yearsInBusiness ?? bi.years_in_business;
    if (y === '' || y == null) return null;
    const n = Number(String(y).replace(/,/g, '').trim());
    if (Number.isFinite(n) && n >= 0) {
      return n === 1 ? '1 year' : `${n} years`;
    }
    const s = String(y).trim();
    return s || null;
  };

  const uniqueStatesFromList = useMemo(() => {
    const set = new Set();
    list.forEach((item) => {
      const st = getItemStateForFilter(item);
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [list]);

  const displayList = useMemo(() => {
    const filtered = list.filter((item) => {
      const bi = item.basic_information || {};
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = (item.company_name || '').toLowerCase();
        const desc = (item.company_description || '').toLowerCase();
        const hq = String(bi.headquartersLocation || bi.headquarters_location || '').toLowerCase();
        if (!name.includes(q) && !desc.includes(q) && !hq.includes(q)) return false;
      }
      if (companyTypeFilter) {
        const ct = String(bi.companyType ?? bi.company_type ?? '').trim();
        if (ct !== companyTypeFilter) return false;
      }
      if (selectedIndustryFilters.length > 0) {
        const itemInd = getItemIndustryValues(item);
        const ok = selectedIndustryFilters.some((sf) => itemInd.includes(sf));
        if (!ok) return false;
      }
      if (selectedStateFilter) {
        if (getItemStateForFilter(item) !== selectedStateFilter) return false;
      }
      if (selectedCoreSpecFilters.length > 0) {
        const specs = getItemCoreSpecializationValues(item);
        const ok = selectedCoreSpecFilters.some((f) => specs.includes(f));
        if (!ok) return false;
      }
      if (selectedSpecialCargoFilters.length > 0) {
        const cargo = getItemSpecialCargoValues(item);
        const ok = selectedSpecialCargoFilters.some((f) => cargo.includes(f));
        if (!ok) return false;
      }
      if (selectedShipmentTypeFilters.length > 0) {
        const ships = getItemPreferredShipmentTypes(item);
        const ok = selectedShipmentTypeFilters.some((f) => ships.includes(f));
        if (!ok) return false;
      }
      if (selectedModeFilters.length > 0) {
        const modes = new Set(getTransportModes(item));
        const ok = selectedModeFilters.some((m) => modes.has(String(m).toLowerCase()));
        if (!ok) return false;
      }
      return true;
    });

    const arr = [...filtered];
    if (sortBy === 'name') {
      arr.sort((a, b) =>
        (a.company_name || '').localeCompare(b.company_name || '', undefined, { sensitivity: 'base' })
      );
    } else if (sortBy === 'rating') {
      arr.sort((a, b) => {
        const ra = parseFloat(getRatingDisplay(a) || '0');
        const rb = parseFloat(getRatingDisplay(b) || '0');
        if (rb !== ra) return rb - ra;
        return (a.company_name || '').localeCompare(b.company_name || '', undefined, { sensitivity: 'base' });
      });
    }
    return arr;
  }, [
    list,
    search,
    selectedIndustryFilters,
    selectedStateFilter,
    companyTypeFilter,
    selectedCoreSpecFilters,
    selectedSpecialCargoFilters,
    selectedShipmentTypeFilters,
    selectedModeFilters,
    sortBy,
  ]);

  const toggleIndustryFilter = (value) => {
    setSelectedIndustryFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const toggleCoreSpecFilter = (value) => {
    setSelectedCoreSpecFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleSpecialCargoFilter = (value) => {
    setSelectedSpecialCargoFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleShipmentTypeFilter = (value) => {
    setSelectedShipmentTypeFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleModeFilter = (value) => {
    setSelectedModeFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const clearSidebarFilters = () => {
    setSelectedIndustryFilters([]);
    setSelectedStateFilter('');
    setCompanyTypeFilter('');
    setSelectedCoreSpecFilters([]);
    setSelectedSpecialCargoFilters([]);
    setSelectedShipmentTypeFilters([]);
    setSelectedModeFilters([]);
  };

  return (
    <div className="lpl-page">
      <B2BHubHomeHeader />
      <div className="lpl-container">
        <nav className="lpl-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="lpl-breadcrumb__sep" aria-hidden>
            &gt;
          </span>
          <span className="lpl-breadcrumb__current" aria-current="page">
            Logistics Providers
          </span>
        </nav>

        <header className="lpl-topbar">
          <div className="lpl-topbar-text">
            <h1 className="lpl-title">{pageTitle}</h1>
            <p className="lpl-subtitle">
              {loading
                ? 'Loading…'
                : `Browse verified logistics partners. Showing ${displayList.length} of ${
                    total || displayList.length
                  } provider${displayList.length !== 1 ? 's' : ''}.`}
            </p>
          </div>
          <div className="lpl-topbar-actions">
            <div className="lpl-search-wrap">
              <FaSearch className="lpl-search-icon" aria-hidden />
              <input
                type="text"
                className="lpl-search-input"
                placeholder="Search by name, HQ, or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search logistics providers"
              />
            </div>
          </div>
        </header>

        <div className="lpl-shell">
          <aside className="lpl-filters" aria-label="Filter providers">
            <h2 className="lpl-filters-title">Filters</h2>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Sort</div>
              <select
                className="form-select lpl-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="relevance">Relevance</option>
                <option value="name">Name (A–Z)</option>
                <option value="rating">Rating (high to low)</option>
              </select>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Transport mode</div>
              <p className="lpl-filter-hint">Same modes as profile → Transport Modes</p>
              <div className="lpl-checklist">
                {LOGISTICS_TRANSPORT_MODES.map((opt) => (
                  <label key={opt.id} className="lpl-checkline">
                    <input
                      type="checkbox"
                      checked={selectedModeFilters.includes(opt.id)}
                      onChange={() => toggleModeFilter(opt.id)}
                    />
                    <span>
                      <span className="me-1" aria-hidden>
                        {opt.icon}
                      </span>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Preferred shipment types</div>
              <p className="lpl-filter-hint">Profile → Transport → Preferred shipment types</p>
              <div className="lpl-checklist lpl-filter-scroll">
                {LOGISTICS_PREFERRED_SHIPMENT_TYPES.map((label) => (
                  <label key={label} className="lpl-checkline lpl-checkline--compact">
                    <input
                      type="checkbox"
                      checked={selectedShipmentTypeFilters.includes(label)}
                      onChange={() => toggleShipmentTypeFilter(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Company type</div>
              <p className="lpl-filter-hint">Profile → Basic Information</p>
              <select
                className="form-select lpl-select"
                value={companyTypeFilter}
                onChange={(e) => setCompanyTypeFilter(e.target.value)}
              >
                <option value="">All company types</option>
                {LOGISTICS_COMPANY_TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Industries served</div>
              <p className="lpl-filter-hint">Profile → Capabilities (match any)</p>
              <div className="lpl-checklist lpl-filter-scroll">
                {LOGISTICS_INDUSTRIES_SERVED.map((label) => (
                  <label key={label} className="lpl-checkline lpl-checkline--compact">
                    <input
                      type="checkbox"
                      checked={selectedIndustryFilters.includes(label)}
                      onChange={() => toggleIndustryFilter(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Core specialization</div>
              <p className="lpl-filter-hint">Profile → Capabilities (match any)</p>
              <div className="lpl-checklist lpl-filter-scroll">
                {LOGISTICS_CORE_SPECIALIZATION.map((label) => (
                  <label key={label} className="lpl-checkline lpl-checkline--compact">
                    <input
                      type="checkbox"
                      checked={selectedCoreSpecFilters.includes(label)}
                      onChange={() => toggleCoreSpecFilter(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Special cargo</div>
              <p className="lpl-filter-hint">Profile → Capabilities (match any)</p>
              <div className="lpl-checklist lpl-filter-scroll">
                {LOGISTICS_SPECIAL_CARGO.map((label) => (
                  <label key={label} className="lpl-checkline lpl-checkline--compact">
                    <input
                      type="checkbox"
                      checked={selectedSpecialCargoFilters.includes(label)}
                      onChange={() => toggleSpecialCargoFilter(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lpl-filter-block">
              <div className="lpl-filter-head">Location (state)</div>
              <p className="lpl-filter-hint">From HQ or company address</p>
              <select
                className="form-select lpl-select"
                value={selectedStateFilter}
                onChange={(e) => setSelectedStateFilter(e.target.value)}
              >
                <option value="">All states</option>
                {uniqueStatesFromList.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <button type="button" className="lpl-btn-clear" onClick={clearSidebarFilters}>
              Clear filters
            </button>
          </aside>

          <main className="lpl-main">
            {loading ? (
              <div className="lpl-loading">Loading providers…</div>
            ) : error ? (
              <div className="lpl-empty-state">
                <div className="lpl-alert mb-3 text-start" role="alert">
                  {error}
                </div>
                <div className="lpl-retry-row">
                  <button type="button" className="lpl-btn lpl-btn-outline" onClick={doRetry}>
                    Retry
                  </button>
                  <Link to="/" className="lpl-btn lpl-btn-primary">
                    Back to Home
                  </Link>
                </div>
              </div>
            ) : (
              <div className="row g-4">
                {displayList.map((item) => {
                  const imgUrl = getCardImage(item);
                  const bi = item.basic_information || {};
                  const platformVerified = (item.verification_status || '').toLowerCase() === 'verified';
                  const providerVerified = !!(bi.verifiedProvider ?? bi.verified_provider);
                  const isVerified = platformVerified || providerVerified;
                  const featured = isFeaturedProvider(item);
                  const rating = getRatingDisplay(item);
                  const reviews = getReviewCount(item);
                  const locationStr = getLocation(item);
                  const tags = getIndustryTags(item);
                  const companyTypeStr = getCompanyTypeDisplay(item);
                  const experienceStr = getExperienceDisplay(item);
                  const modes = getTransportModes(item);
                  const providerCid = Number(item.company_id);
                  const apiMeta = mySubmissionsByProviderId[providerCid];
                  /**
                   * Logged-in: only DB-backed `/my-submissions` (per user, per provider). Ignore sessionStorage
                   * so another account on the same browser or stale keys never show the wrong button.
                   * Logged-out: sessionStorage from this browser after anonymous submit (no API row for user id).
                   */
                  const isLoggedIn = Boolean(StorageService.getToken());
                  const viewRfqPath = isLoggedIn
                    ? buildLogisticsViewRfqPathFromIds(
                        providerCid,
                        apiMeta?.id,
                        apiMeta?.rfqReferenceNumber,
                        "providers",
                      )
                    : buildLogisticsViewRfqPath(item.company_id) ||
                      buildLogisticsViewRfqPathFromIds(
                        providerCid,
                        apiMeta?.id,
                        apiMeta?.rfqReferenceNumber,
                        "providers",
                      );
                  return (
                    <div key={item.company_id} className="col-12 col-md-6 col-xl-4">
                      <div className="lpl-card">
                        <div className="lpl-card-image-wrap">
                          {imgUrl ? (
                            <img
                              src={resolvePublicFileUrl(imgUrl)}
                              alt=""
                              className="lpl-card-image"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="lpl-card-image-placeholder" aria-hidden />
                          )}
                          {featured ? <span className="lpl-card-badge">Featured</span> : null}
                        </div>
                        <div className="lpl-card-body">
                          <div className="lpl-card-title-row">
                            <h3 className="lpl-card-title">{item.company_name || 'Logistics provider'}</h3>
                            {isVerified ? (
                              <span className="lpl-card-verified" title="Verified">
                                <FaShieldAlt aria-hidden />
                              </span>
                            ) : null}
                          </div>
                          <div className="lpl-card-rating">
                            <FaStar className="lpl-star" aria-hidden />
                            {rating != null ? (
                              <>
                                {' '}
                                {rating}
                                <span className="lpl-reviews">
                                  {reviews != null ? ` (${reviews} reviews)` : ''}
                                </span>
                              </>
                            ) : (
                              <span className="lpl-reviews-muted">No ratings yet</span>
                            )}
                          </div>
                          {locationStr ? (
                            <p className="lpl-card-location">
                              <FaMapMarkerAlt className="lpl-pin" aria-hidden />
                              {locationStr}
                            </p>
                          ) : (
                            <p className={`lpl-card-location ${locationStr ? '' : 'lpl-card-location-muted'}`}>
                              <FaMapMarkerAlt className="lpl-pin" aria-hidden />
                              Location not specified
                            </p>
                          )}
                          {modes.length > 0 ? (
                            <div className="lpl-card-tags">
                              {modes.slice(0, 4).map((m) => (
                                <span key={m} className="lpl-card-tag lpl-card-tag--mode">
                                  {m}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {tags.length > 0 ? (
                            <div className="lpl-card-tags">
                              {tags.map((t) => (
                                <span key={t} className="lpl-card-tag">
                                  {t}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <div className="lpl-metrics">
                            <div className="lpl-metric-row">
                              <span className="lpl-metric-label">Company type</span>
                              <span className="lpl-metric-value">{formatMetric(companyTypeStr)}</span>
                            </div>
                            <div className="lpl-metric-row">
                              <span className="lpl-metric-label">Experience</span>
                              <span className="lpl-metric-value">{formatMetric(experienceStr)}</span>
                            </div>
                          </div>
                          <div className="lpl-card-actions">
                            <Link
                              to={`/logistics-providers/profile/${item.company_id}`}
                              className="lpl-btn lpl-btn-outline"
                            >
                              <FaEye aria-hidden />
                              View
                            </Link>
                            {viewRfqPath ? (
                              <Link
                                to={viewRfqPath}
                                state={{ from: 'providers' }}
                                className="lpl-btn lpl-btn-primary lpl-card-view-rfq-btn"
                              >
                                <FaFileAlt aria-hidden />
                                View RFQ
                              </Link>
                            ) : (
                              <button
                                type="button"
                                className="lpl-btn lpl-btn-primary lpl-card-send-rfq-btn"
                                onClick={() => setRfqModalCompanyId(item.company_id)}
                              >
                                <FaPaperPlane aria-hidden />
                                Send RFQ
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!loading && displayList.length === 0 && !error ? (
              <div className="lpl-empty-state">No logistics providers match your filters.</div>
            ) : null}
          </main>
        </div>
      </div>

      <LogisticsRfqTypeSelectionModal
        open={rfqModalCompanyId != null}
        companyId={rfqModalCompanyId}
        onClose={() => setRfqModalCompanyId(null)}
      />

      <HubHomeFooter />
    </div>
  );
}
